import { ButtonFlavor, ButtonType, ExButton, ExLink, LinkSize } from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    type CreateIdentityProvider,
    type IdentityProviderDetail,
    deleteIdentityProvider,
    fetchIdentityProviderById,
    updateIdentityProvider,
} from '../identityProviders';
import ErrorAlertBanner from '../../shared/ErrorAlertBanner';
import styles from '../../shared/Form.module.css';
import { GENERIC_ERROR_MESSAGE } from '../../shared/apiUtils';
import IdentityProviderFormFields, {
    type FormValues,
    EMPTY_FORM_VALUES,
} from './IdentityProviderFormFields';

const valuesFromDetail = (data: IdentityProviderDetail): FormValues => ({
    ...EMPTY_FORM_VALUES,
    name: data.name,
    allowedAudience: data.allowedAudience,
    clientId: data.clientId,
    clientSecret: data.clientSecret,
    wellKnownUrl: data.wellKnownUrl,
});

interface IdentityProviderEditProps {
    identityProviderId?: string;
    onClose?: () => void;
}

const IdentityProviderEdit = ({ identityProviderId, onClose }: IdentityProviderEditProps = {}) => {
    const params = useParams<{ id: string }>();
    const navigate = useNavigate();
    const id = identityProviderId ?? params.id;
    const close = onClose ?? (() => navigate('/identity-providers'));

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [provider, setProvider] = useState<IdentityProviderDetail | null>(null);
    const [values, setValues] = useState<FormValues>(EMPTY_FORM_VALUES);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const [deleteState, setDeleteState] = useState<'idle' | 'confirming'>('idle');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (!id) return;
        fetchIdentityProviderById(id)
            .then((data) => {
                setProvider(data);
                setValues(valuesFromDetail(data));
            })
            .catch(() => setLoadError('Failed to load identity provider.'))
            .finally(() => setLoading(false));
    }, [id]);

    const handleChange = (patch: Partial<FormValues>) =>
        setValues((prev) => ({ ...prev, ...patch }));

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!provider || !id) return;
        setSubmitError(null);
        setSubmitting(true);

        const payload: CreateIdentityProvider = {
            name: values.name,
            allowedAudience: values.allowedAudience,
            clientId: values.clientId,
            clientSecret: values.clientSecret,
            wellKnownUrl: values.wellKnownUrl,
        };

        try {
            await updateIdentityProvider(id, payload);
            close();
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className={styles.header}>
                <ExLink
                    data-pendo-link="idp-edit-back"
                    onClick={() => close()}
                    leadingIcon={'Left arrow'}
                >
                    Identity Providers
                </ExLink>
                <div className={styles.headingRow}>
                    <h2 className={`${styles.heading} ex-text-h2`}>Edit Identity Provider</h2>
                    {!loading && provider && deleteState === 'idle' && (
                        <ExButton
                            data-pendo-button="idp-edit-delete"
                            type={ButtonType.SECONDARY}
                            flavor={ButtonFlavor.RISKY}
                            onClick={() => setDeleteState('confirming')}
                        >
                            Delete
                        </ExButton>
                    )}
                </div>
                {!loading && provider && deleteState === 'confirming' && (
                    <div className={styles.deleteConfirmRow}>
                        <span className={styles.deleteConfirmText}>Delete this provider?</span>
                        <button
                            data-pendo-button="idp-edit-deletecancel"
                            type="button"
                            className={styles.cancelButton}
                            onClick={() => setDeleteState('idle')}
                        >
                            No, keep
                        </button>
                        <ExButton
                            data-pendo-button="idp-edit-deleteconfirm"
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.RISKY}
                            disabled={isDeleting}
                            onClick={async () => {
                                if (!id) return;
                                setIsDeleting(true);
                                try {
                                    await deleteIdentityProvider(id);
                                    close();
                                } catch (err) {
                                    setIsDeleting(false);
                                    setDeleteState('idle');
                                    setSubmitError(
                                        err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE,
                                    );
                                }
                            }}
                        >
                            {isDeleting ? 'Deleting…' : 'Yes, delete'}
                        </ExButton>
                    </div>
                )}
            </div>

            {loading && <p className={styles.hint}>Loading…</p>}
            {loadError && (
                <ErrorAlertBanner onClose={() => setLoadError(null)}>{loadError}</ErrorAlertBanner>
            )}

            {!loading && provider && (
                <form className={styles.form} onSubmit={handleSubmit}>
                    <IdentityProviderFormFields values={values} onChange={handleChange} />

                    {submitError && (
                        <ErrorAlertBanner
                            className={styles.saveError}
                            onClose={() => setSubmitError(null)}
                        >
                            {submitError}
                        </ErrorAlertBanner>
                    )}

                    <div className={styles.actions}>
                        <ExLink
                            data-pendo-link="idp-edit-cancel"
                            size={LinkSize.XSMALL}
                            onClick={() => close()}
                        >
                            Cancel
                        </ExLink>
                        <ExButton
                            data-pendo-button="idp-edit-save"
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            disabled={submitting}
                            onClick={() => handleSubmit()}
                        >
                            {submitting ? 'Saving…' : 'Save Changes'}
                        </ExButton>
                    </div>
                </form>
            )}
        </>
    );
};

export default IdentityProviderEdit;
